import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	PaymentProvider,
} from "../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import LabeledField from "../../../../../../components/LabeledField";
import { getPaymentProviderDisplayName } from "../../utils/getPaymentProviderDisplayName";
import { ACQUIRING_PROVIDERS } from "../../constants/providers";

import InternalController from "./Controller";

const SelectPaymentSystemsBase = react.withController<
	SelectPaymentSystems.PropsBase,
	SelectPaymentSystems.Controller
>(({ value, onChange, controller, providers }) => {
	const [error, setError] = useState<string>("");
	controller.setContext({ value, setError });

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const paymentProvider = valueEditor.useGetter("paymentProvider");
	const paymentProviderSet = valueEditor.useSetter("paymentProvider");

	const selectOptionsWrap = useCallback(
		(provider: PaymentProvider) => ({
			key: provider,
			value: provider,
			label: getPaymentProviderDisplayName(provider, t),
		}),
		[t],
	);

	const options = useMemo(
		() =>
			// Default list is acquiring only: terminal networks (EasyPay, OTV)
			// have their own page and pass `providers` explicitly.
			(providers ?? ACQUIRING_PROVIDERS).map(selectOptionsWrap),
		[providers, selectOptionsWrap],
	);

	const updateSelectedValues = useCallback(
		(value) => paymentProviderSet(value),
		[paymentProviderSet],
	);

	return (
		<LabeledField
			label={
				t(
					"pages.settings.pages.paymentSystem.selectPaymentSystems.str0",
				) ?? ""
			}
		>
			<Select
				// style={{ width: "200px" }}
				placeholder={
					t(
						"pages.settings.pages.paymentSystem.selectPaymentSystems.selectPaymentProvider",
					) ?? ""
				}
				error={error}
				value={paymentProvider}
				options={options}
				onChange={updateSelectedValues}
			/>
		</LabeledField>
	);
}, InternalController);
const SelectPaymentSystems = memo(SelectPaymentSystemsBase);

declare namespace SelectPaymentSystems {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		providers?: PaymentProvider[];
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default SelectPaymentSystems;
